import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { projectApi } from '../../../../api/projectApi';
import { useToast } from '../../../../context/ToastContext';
import { STATUS_OPTIONS } from '../constants';

/**
 * Custom hook for managing the state and business logic of the ProjectList component.
 * 
 * @param {Object} params
 * @param {string} params.variant - The variant of the list ('admin' or 'tool').
 * @param {Function} params.onProjectSelect - Callback when a project is selected.
 * @returns {Object} The state and handler functions.
 */
export function useProjectList({ variant = 'admin', onProjectSelect } = {}) {
    const { showToast } = useToast();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'

    // Main Projects State
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS.filter(o => o.value !== 'archived').map(o => o.value));
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterRef = useRef(null);
    const [activeSort, setActiveSort] = useState({ key: 'updated_at', direction: 'desc' });
    const [archiveSort, setArchiveSort] = useState({ key: 'updated_at', direction: 'desc' });
    const [trashSort, setTrashSort] = useState({ key: 'updated_at', direction: 'desc' });

    // Archive Specific State
    const [isArchiveOpen, setIsArchiveOpen] = useState(false);
    const [isTrashOpen, setIsTrashOpen] = useState(false);
    const [isListOpen, setIsListOpen] = useState(true);
    const [archiveSearch, setArchiveSearch] = useState('');
    const [trashSearch, setTrashSearch] = useState('');
    const [focusedProject, setFocusedProject] = useState(null);
    const [deletedProjects, setDeletedProjects] = useState([]);

    // Global Permissions
    const [globalPermissions, setGlobalPermissions] = useState({
        create_projects: false,
        delete_projects: false,
        edit_projects: false
    });

    // Modals
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [projectMode, setProjectMode] = useState('create');
    const [projectForm, setProjectForm] = useState(null);

    const [showUserModal, setShowUserModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    // Bulk Actions
    const [selectedProjectIds, setSelectedProjectIds] = useState([]);

    // URL params
    const [searchParams] = useSearchParams();
    const [hasOpenedFromUrl, setHasOpenedFromUrl] = useState(false);

    useEffect(() => {
        function handleClickOutside(event) {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setIsFilterOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        
        // Restore Project Modal State if returning from deep link
        const isProjectsPage = window.location.pathname.includes('/verwaltung/projekte');
        const backupStr = isProjectsPage ? sessionStorage.getItem('projectModalBackup') : null;
        if (backupStr) {
            try {
                const backup = JSON.parse(backupStr);
                sessionStorage.removeItem('projectModalBackup');
                if (backup.isOpen) {
                    setProjectMode(backup.currentMode);
                    setProjectForm(backup.project);
                    if (backup.project?.id) {
                        setSelectedProject(backup.project);
                    }
                    setShowProjectModal(true);
                    
                    // Relay the internal states (tab & tempTeamMembers) to ProjectDetailModal
                    sessionStorage.setItem('projectModalBackup_internal', JSON.stringify({
                        activeTab: backup.activeTab,
                        tempTeamMembers: backup.tempTeamMembers,
                        currentMode: backup.currentMode,
                        teamTabsState: backup.teamTabsState
                    }));
                }
            } catch(e) {}
        }
        
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const loadGlobalPermissions = useCallback(async () => {
        try {
            const data = await projectApi.getPermissions();
            setGlobalPermissions(data);
        } catch (err) { console.error(err); }
    }, []);

    const loadProjects = useCallback(async () => {
        setLoading(true);
        try {
            const [activeData, deletedData] = await Promise.all([
                projectApi.getAllStatus(),
                projectApi.getDeletedProjects()
            ]);
            setProjects(activeData);
            setDeletedProjects(deletedData);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        loadProjects();
        loadGlobalPermissions();
    }, [loadProjects, loadGlobalPermissions]);

    // Open project from URL if id is provided
    useEffect(() => {
        const projectIdFromUrl = searchParams.get('id');
        if (projects.length > 0 && projectIdFromUrl && !hasOpenedFromUrl) {
            const proj = projects.find(p => p.id === projectIdFromUrl);
            if (proj) {
                setSelectedProject(proj);
                // Allow edit if they have permissions
                const canEdit = proj.can_edit_project || globalPermissions.edit_projects;
                setProjectMode(canEdit ? 'edit' : 'view');
                setShowProjectModal(true);
            }
            setHasOpenedFromUrl(true); // Ensure we only try once
        }
    }, [projects, searchParams, hasOpenedFromUrl, globalPermissions]);

    // Sync filter when switching views (archived only for map)
    useEffect(() => {
        if (viewMode === 'list' && statusFilter.includes('archived')) {
            setStatusFilter(prev => prev.filter(s => s !== 'archived'));
        }
    }, [viewMode, statusFilter]);

    // On entering map mode: If 'All' was selected in list, include archived
    useEffect(() => {
        if (viewMode === 'map') {
            const activeValues = STATUS_OPTIONS.filter(o => o.value !== 'archived').map(o => o.value);
            const allActiveSelected = activeValues.every(val => statusFilter.includes(val));
            if (allActiveSelected && !statusFilter.includes('archived')) {
                setStatusFilter(prev => [...prev, 'archived']);
            }
        }
    }, [viewMode]);

    // --- LOGIC HELPERS ---
    const filterProjects = (list, term, statusArr) => {
        let result = list;
        if (statusArr) {
            result = result.filter(p => statusArr.includes(p.status));
        }
        if (term) {
            const lower = term.toLowerCase();
            result = result.filter(p =>
                (p.name && p.name.toLowerCase().includes(lower)) ||
                (p.project_number && p.project_number.toLowerCase().includes(lower)) ||
                (p.address && p.address.toLowerCase().includes(lower))
            );
        }
        return result;
    };

    const sortProjects = (list, config) => {
        if (!config || !config.key) return list;
        return [...list].sort((a, b) => {
            let aVal = a[config.key] || '';
            let bVal = b[config.key] || '';
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();
            if (aVal < bVal) return config.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return config.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const allFilteredProjects = sortProjects(filterProjects(projects, searchTerm, statusFilter), activeSort);
    const activeList = allFilteredProjects.filter(p => p.status !== 'archived');
    const archivedList = sortProjects(filterProjects(projects.filter(p => p.status === 'archived'), archiveSearch, null), archiveSort);
    const trashList = sortProjects(filterProjects(deletedProjects, trashSearch, null), trashSort);

    const handleSortActive = (key) => {
        let direction = 'asc';
        if (activeSort.key === key && activeSort.direction === 'asc') direction = 'desc';
        setActiveSort({ key, direction });
    };

    const handleSortArchive = (key) => {
        let direction = 'asc';
        if (archiveSort.key === key && archiveSort.direction === 'asc') direction = 'desc';
        setArchiveSort({ key, direction });
    };

    const handleSortTrash = (key) => {
        let direction = 'asc';
        if (trashSort.key === key && trashSort.direction === 'asc') direction = 'desc';
        setTrashSort({ key, direction });
    };

    const toggleStatusFilter = (value) => {
        if (statusFilter.includes(value)) setStatusFilter(statusFilter.filter(s => s !== value));
        else setStatusFilter([...statusFilter, value]);
    };

    const handleSelectAllFilters = () => {
        const availableOptions = viewMode === 'map'
            ? STATUS_OPTIONS.map(o => o.value)
            : STATUS_OPTIONS.filter(o => o.value !== 'archived').map(o => o.value);
        setStatusFilter(availableOptions);
    };

    const handleSelectNoneFilters = () => {
        setStatusFilter([]);
    };

    const getFilterLabel = () => {
        if (statusFilter.length === 0) return 'Keine ausgewählt';
        const availableOptions = viewMode === 'map'
            ? STATUS_OPTIONS
            : STATUS_OPTIONS.filter(o => o.value !== 'archived');

        if (statusFilter.length === availableOptions.length) return 'Alle anzeigen';
        if (statusFilter.length === 1) return STATUS_OPTIONS.find(o => o.value === statusFilter[0])?.label;
        return `${statusFilter.length} ausgewählt`;
    };

    // --- ACTIONS ---
    const openCreateProject = () => {
        setProjectMode('create');
        setProjectForm({ name: '', project_number: '', address: '', description: '', status: 'active', latitude: null, longitude: null });
        setSelectedProject(null);
        setShowProjectModal(true);
    };

    const openViewProject = (p, mode = 'view') => {
        setProjectMode(mode);
        setProjectForm({ ...p });
        setShowProjectModal(true);
    };

    const handleDeleteProject = async (project) => {
        if (!window.confirm(`Projekt "${project.name}" wirklich löschen?`)) return;
        try {
            await projectApi.deleteProject(project.id);
            setShowProjectModal(false);
            loadProjects();
            
            showToast(`Projekt "${project.name}" wurde in den Papierkorb verschoben`, 'success', 6000, {
                label: 'Rückgängig',
                onClick: async () => {
                    try {
                        await projectApi.restoreProject(project.id);
                        loadProjects();
                        showToast('Projekt wiederhergestellt', 'success');
                    } catch (err) {
                        showToast('Fehler bei Wiederherstellung', 'error');
                    }
                }
            });
        } catch (err) {
            showToast('Fehler beim Löschen: ' + (err.response?.data?.error || 'Unbekannt'), 'error');
        }
    };

    const handleBulkStatusChange = async (newStatus) => {
        if (selectedProjectIds.length === 0 || !window.confirm(`${selectedProjectIds.length} Projekte ändern?`)) return;
        try {
            await projectApi.bulkUpdateStatus(selectedProjectIds, newStatus);
            setSelectedProjectIds([]);
            loadProjects();
            showToast(`${selectedProjectIds.length} Projekte wurden aktualisiert`, 'success');
        } catch (err) { showToast('Fehler bei der Aktion', 'error'); }
    };

    const handleBulkDelete = async () => {
        const count = selectedProjectIds.length;
        if (count === 0 || !window.confirm(`${count} Projekte löschen?`)) return;
        
        const idsToRestore = [...selectedProjectIds];
        try {
            await projectApi.bulkDelete(selectedProjectIds);
            setSelectedProjectIds([]);
            loadProjects();
            
            showToast(`${count} Projekte wurden in den Papierkorb verschoben`, 'success', 6000, {
                label: 'Rückgängig',
                onClick: async () => {
                    try {
                        for (const id of idsToRestore) {
                            await projectApi.restoreProject(id);
                        }
                        loadProjects();
                        showToast(`${count} Projekte wiederhergestellt`, 'success');
                    } catch (err) {
                        showToast('Fehler bei Wiederherstellung', 'error');
                    }
                }
            });
        } catch (err) { showToast('Fehler bei der Aktion', 'error'); }
    };

    const handleRestoreProject = async (id) => {
        try {
            await projectApi.restoreProject(id);
            setSelectedProjectIds(prev => prev.filter(pid => pid !== id));
            loadProjects();
            showToast('Projekt wiederhergestellt', 'success');
        } catch (err) {
            showToast('Fehler bei der Wiederherstellung', 'error');
        }
    };

    const handleBulkRestore = async () => {
        const count = selectedProjectIds.length;
        if (count === 0) return;
        try {
            for (const id of selectedProjectIds) {
                await projectApi.restoreProject(id);
            }
            setSelectedProjectIds([]);
            loadProjects();
            showToast(`${count} Projekte wiederhergestellt`, 'success');
        } catch (err) {
            showToast('Fehler bei der Wiederherstellung', 'error');
        }
    };

    const handleHardDelete = async (id) => {
        if (!window.confirm('Projekt wirklich UNWIDERRUFLICH löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
        try {
            await projectApi.deleteProject(id, true);
            setSelectedProjectIds(prev => prev.filter(pid => pid !== id));
            loadProjects();
            showToast('Projekt dauerhaft gelöscht', 'success');
        } catch (err) {
            showToast('Fehler beim dauerhaften Löschen', 'error');
        }
    };

    const handleBulkHardDelete = async () => {
        const count = selectedProjectIds.length;
        if (count === 0 || !window.confirm(`${count} Projekte wirklich UNWIDERRUFLICH löschen?`)) return;
        try {
            await projectApi.bulkDelete(selectedProjectIds, true);
            setSelectedProjectIds([]);
            loadProjects();
            showToast(`${count} Projekte dauerhaft gelöscht`, 'success');
        } catch (err) {
            showToast('Fehler bei der Aktion', 'error');
        }
    };

    const handleProjectSubmit = async (formData, mode, teamMembers) => {
        try {
            if (mode === 'create') {
                const res = await projectApi.createProject(formData);
                if (teamMembers && teamMembers.length > 0) {
                    for (const user of teamMembers) {
                        await projectApi.addProjectUser(res.id, {
                            user_id: user.user_id,
                            worker_id: user.worker_id,
                            role: user.role || user.project_role || 'employee',
                            permissions: user.permissions
                        });
                    }
                }
            } else {
                await projectApi.updateProject(formData.id, formData);
                await projectApi.updateProjectStatus(formData.id, formData.status);
            }
            setShowProjectModal(false);
            loadProjects();
            showToast(mode === 'create' ? 'Projekt erfolgreich erstellt' : 'Änderungen gespeichert', 'success');
        } catch (err) {
            const errorMessage = err.response?.data?.error || 'Fehler';
            if (errorMessage.includes('Projektnummer')) {
                return;
            }
            showToast(errorMessage, 'error');
        }
    };

    return {
        projects,
        loading,
        viewMode,
        searchTerm,
        statusFilter,
        isFilterOpen,
        filterRef,
        isArchiveOpen,
        isTrashOpen,
        isListOpen,
        archiveSearch,
        trashSearch,
        focusedProject,
        globalPermissions,
        showProjectModal,
        projectMode,
        projectForm,
        showUserModal,
        selectedProject,
        selectedProjectIds,
        activeSort,
        archiveSort,
        trashSort,
        handleSortActive,
        handleSortArchive,
        handleSortTrash,
        setSearchTerm,
        setStatusFilter,
        setIsFilterOpen,
        setViewMode,
        setIsArchiveOpen,
        setIsTrashOpen,
        setIsListOpen,
        setArchiveSearch,
        setTrashSearch,
        setFocusedProject,
        setSelectedProjectIds,
        setShowProjectModal,
        setSelectedProject,
        setProjectMode,
        toggleStatusFilter,
        handleSelectAllFilters,
        handleSelectNoneFilters,
        getFilterLabel,
        openCreateProject,
        openViewProject,
        handleDeleteProject,
        handleRestoreProject,
        handleBulkRestore,
        handleHardDelete,
        handleBulkHardDelete,
        handleBulkStatusChange,
        handleBulkDelete,
        handleProjectSubmit,
        loadProjects,
        activeList,
        archivedList,
        trashList,
        deletedProjects,
        allFilteredProjects
    };
}
